import React, { useState } from 'react';
import { Spinner, TextInput } from 'flowbite-react';
import { HiOutlineSearch } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

import { lookupTickets } from './api';
import { statusText, ticketPath } from './helpers';
import { printShopStrings } from './strings';
import { TicketSummary } from './types';

/**
 * "What happened with order 0000071234?"
 *
 * Deliberately a SEPARATE surface from the queue search above it, because the
 * two answer different questions. The queue search filters the work an operator
 * has today. This reaches every order whatever its status — including imported
 * records, which are years old and are not in the queue at all.
 *
 * Collapsed by default: the queue is the page's job, and this is the thing you
 * open when somebody asks you a question about the past.
 */
export const PastOrderLookup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<TicketSummary[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  const search = async () => {
    const trimmed = term.trim();
    if (!trimmed) {
      return;
    }
    setIsSearching(true);
    setFailed(false);
    try {
      // One box, because a member gives you whichever they have. An address is
      // the only one of the two that can contain an "@".
      const page = await lookupTickets(
        trimmed.includes('@')
          ? { email: trimmed }
          : { orderNumber: trimmed },
      );
      setResults(page.data);
    } catch {
      setFailed(true);
      setResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div
      className="border-b border-neutral-100 px-4 py-3"
      data-testid="past-order-lookup"
    >
      <button
        type="button"
        data-testid="past-order-lookup-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="text-sm font-semibold text-primary-700 underline"
      >
        {printShopStrings.archive.lookupTitle}
      </button>

      {open && (
        <div className="mt-3">
          <p className="mb-2 text-xs text-neutral-500">
            {printShopStrings.archive.lookupHint}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <TextInput
              data-testid="past-order-lookup-input"
              icon={HiOutlineSearch}
              placeholder={printShopStrings.archive.lookupPlaceholder}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void search();
                }
              }}
              className="max-w-md flex-1"
            />
            <button
              type="button"
              data-testid="past-order-lookup-submit"
              disabled={isSearching || term.trim() === ''}
              onClick={() => void search()}
              className="rounded-lg border border-primary-500 px-4 py-2 text-sm font-semibold text-primary-700 disabled:opacity-60"
            >
              {isSearching
                ? printShopStrings.archive.lookupSearching
                : printShopStrings.archive.lookupButton}
            </button>
            {isSearching && <Spinner size="sm" />}
          </div>

          {failed && (
            <p
              data-testid="past-order-lookup-error"
              className="mt-2 text-sm text-red-600"
            >
              {printShopStrings.archive.lookupFailed}
            </p>
          )}

          {results !== null && !failed && (
            <div className="mt-3" data-testid="past-order-lookup-results">
              {results.length === 0 ? (
                <p
                  data-testid="past-order-lookup-empty"
                  className="text-sm text-neutral-500"
                >
                  {printShopStrings.archive.lookupEmpty}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {results.map((ticket) => (
                    <li key={ticket.orderId}>
                      <button
                        type="button"
                        data-testid={`past-order-result-${ticket.orderId}`}
                        onClick={() => navigate(ticketPath(ticket.orderId))}
                        className="flex w-full flex-wrap items-center gap-3 rounded-md border border-neutral-200 px-3 py-2 text-left text-sm hover:bg-neutral-50"
                      >
                        <span className="font-mono font-semibold">
                          {ticket.afyOrderNumber ?? ticket.orderId}
                        </span>
                        <span className="text-neutral-700">
                          {ticket.member.name}
                        </span>
                        <span className="text-neutral-500">
                          {ticket.assetTitle}
                        </span>
                        {/* The status is the point for a lookup result: it is
                            how the operator knows at a glance whether this is
                            work or a record. */}
                        <span className="ml-auto text-xs font-semibold uppercase text-neutral-500">
                          {statusText(ticket.status)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
